<template>
  <div>
    <Card class="mb-4">
      <template #title>
        <div class="d-flex align-items-center">
          <i class="pi pi-bell me-2"></i>
          <span class="text-muted">新着通知</span>
          <span
            v-if="unreadNotifications.length"
            class="badge ms-2 bg-grass-green"
            >{{ unreadNotifications.length }}</span
          >
        </div>
      </template>
      <template #content>
        <NotificationTable
          v-if="unreadNotifications.length"
          :notifications="unreadNotifications"
          :max-visible-count="3"
          class="mt-2"
          @mark-as-read="handleNotificationRead"
          @goto-notification="gotoNotification"
        />
        <div v-else class="">新着はありません</div>
      </template>
    </Card>
    <template v-if="menuItems.filter((i) => !!i.visible).length">
      <IndexMenuItem :menu-items="menuItems" class="" />
    </template>
    <IndexMenuItem
      v-if="userMenuItems.filter((i) => !!i.visible)"
      :menu-items="userMenuItems"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from "vue";
import { useToast } from "primevue/usetoast";
import { useRouterUtil, useUser, useAuthorize } from "/@/vue/composables";
import { MenuItems as IndexMenuItem } from "/@/vue/components/Organisms/Index";
import { Table as NotificationTable } from "/@/vue/components/Organisms/Users/Notifications";
import Card from "primevue/card";

const { currentRouteQuery, dropQuery, reload, goto } = useRouterUtil();
const { gotoNotifiable } = useUser();

function gotoNotification(notificationId: number) {
  const notification = unreadNotifications.value.find(
    (n) => n.id === notificationId
  );

  if (notification) {
    gotoNotifiable(notification);
  }
}

const toast = useToast();

function showSuccessToast() {
  toast.add({
    severity: "success",
    summary: "認証成功",
    detail: "メールアドレスの認証に成功しました。",
    group: "br",
  });
}

onMounted(() => {
  if (currentRouteQuery.value.confirmation) {
    showSuccessToast();
  }

  dropQuery();
});

// user

const {
  getUser,
  isOffice,
  isAllLManager,
  isAllManager,
  isAllPoster,
  isIManager,
  isABCManager,
  isABCPoster,
  isABCTManager,
  isUserManager,
  isOrganizationManager,
  isVManager,
  isBcpManager,
  isRuleManager,
  isDManager,
  isSomeManager,
  unreadNotifications,
  updateNotification,
} = useUser();
const { data: user } = getUser();

const menuItems = [
  {
    label: "管理者情報",
    icon: "pi pi-sparkles",
    to: { name: "UsersSystemAdminShow" },
    tabKey: "top",
    visible: isSomeManager.value,
  },
  {
    label: "お知らせ管理",
    icon: "pi pi-bell",
    to: { name: "UsersNewsIndex" },
    tabKey: "top",
    visible: isAllPoster.value || isABCPoster.value,
  },
  {
    label: "組織管理",
    icon: "pi pi-sitemap",
    to: { name: "UsersOrganizationsIndex" },
    tabKey: "top",
    visible: !!isABCManager.value || !!isOrganizationManager.value,
  },
  {
    label: "会員管理",
    icon: "pi pi-users",
    to: { name: "UsersSystemAdminUsers" },
    tabKey: "top",
    visible:
      !!isDManager.value ||
      !!isOffice.value ||
      !!isABCManager.value ||
      !!isUserManager.value ||
      !!isAllLManager.value,
  },
];

const { logout } = useAuthorize();

const userMenuItems = computed(() => {
  if (user.value) {
    return [
      {
        label: "広報誌『わ』管理",
        icon: "pi pi-book",
        to: { name: "UsersMagazinesIndex" },
        tabKey: "top",
        visible: !!isRuleManager.value,
      },
      {
        label: "研修管理",
        icon: "pi pi-video",
        to: { name: "UsersSystemAdminTrainingsIndex" },
        tabKey: "top",
        visible: !!isABCTManager.value,
      },
      {
        label: "賛助会員管理",
        icon: "pi pi-users",
        to: { name: "UsersSponsorsIndex" },
        tabKey: "top",
        visible: !!isRuleManager.value,
      },
      {
        label: "ＢＣＰ管理",
        icon: "pi pi-file-check",
        to: { name: "UsersSystemAdminBcpIndex" },
        tabKey: "top",
        visible: !!isBcpManager.value,
      },
      {
        label: "協会書類等管理",
        icon: "pi pi-inbox",
        to: { name: "UsersSystemAdminRulesIndex" },
        tabKey: "top",
        visible: !!isRuleManager.value,
      },
      {
        label: "お問い合わせ管理",
        icon: "pi pi-info-circle",
        to: { name: "UsersInquiriesIndex" },
        tabKey: "top",
        visible: !!isIManager.value,
      },
      /*
      {
        label: "申請済ボランティア",
        icon: "pi pi-briefcase",
        visible: true,
        to: {
          name: "UsersJoinUsersIndex",
        },
        tabKey: "top",
      },
      {
        label: "ボランティア申請",
        icon: "pi pi-briefcase",
        visible: true,
        to: {
          name: "UsersRecruitmentsIndex",
        },
      },
      */
      {
        label: "ログアウト",
        icon: "pi pi-sign-out",
        tabKey: "top",
        command: async () => {
          await logout();
          reload();
        },
      },
    ];
  } else {
    return [
      {
        label: "会員の方",
        icon: "pi pi-sign-in",
        to: { name: "SignIn" },
        tabKey: "top",
      },
    ];
  }
});

// notification

async function handleNotificationRead(id: number) {
  if (await updateNotification(id, { read: true })) {
    toast.add({
      severity: "success",
      summary: "成功",
      detail: "通知を既読にしました。",
      group: "br",
    });
  } else {
    toast.add({
      severity: "error",
      summary: "エラー",
      detail: "通知の更新に失敗しました。",
      group: "br",
    });
  }
}
</script>

<style scoped lang="scss"></style>
